import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { PlusOutlined, HolderOutlined } from "@ant-design/icons";
import { Modal, Select, Button, Spin } from "antd";
import TravelIcon from "../../Assets/homepageicons/travel.svg";
import HealthIcon from "../../Assets/homepageicons/health.svg";
import RetailIcon from "../../Assets/homepageicons/retail.svg";
import ServicesIcon from "../../Assets/homepageicons/services.svg";
import ActivitiesIcon from "../../Assets/homepageicons/activities.svg";
import FoodAndBeverageIcon from "../../Assets/homepageicons/food&beverage.svg";
import OnlineIcon from "../../Assets/homepageicons/online.svg";
import { ReactComponent as DeleteIcon } from "../../Assets/Icons/deleteIcon.svg";
import { rearrangeHomeFilters, getHomeFilters, deleteHomeFilter, addHomeFilter, getAllCategories } from "../../services/offerService";
import toast from "../../utils/toast";

// Helper to normalize titles for consistent comparison (handles spaces, casing, and special chars)
const normalizeTitle = (title) => {
    if (!title) return "";
    let t = title.toLowerCase().trim();
    // Standardize common variations
    if (t === "health and wellbeing") t = "health & wellbeing";
    if (t === "food and beverage") t = "food & beverage";
    return t;
};

// Check if a category is already active in the tiles list
const isCategoryActive = (catTitle, activeTiles) => {
    const normalizedCat = normalizeTitle(catTitle);
    return activeTiles.some(tile => {
        const normalizedTile = normalizeTitle(tile.title);
        return (
            normalizedTile === normalizedCat ||
            normalizedTile === normalizedCat + "s" ||
            normalizedCat === normalizedTile + "s"
        );
    });
};

const iconMap = {
    "Travel": TravelIcon,
    "Health & Wellbeing": HealthIcon,
    "Retail": RetailIcon,
    "Services": ServicesIcon,
    "Activities": ActivitiesIcon,
    "Food & Beverage": FoodAndBeverageIcon,
    "Online Offers": OnlineIcon,
};

// Map of which local icon file URL belongs to each category
const iconFileMap = {
    "Travel": TravelIcon,
    "Health & Wellbeing": HealthIcon,
    "Retail": RetailIcon,
    "Services": ServicesIcon,
    "Activities": ActivitiesIcon,
    "Food & Beverage": FoodAndBeverageIcon,
    "Online Offers": OnlineIcon,
};

// Fixed static image paths sent to the backend (environment-independent)
const staticIconPathMap = {
    "Health & Wellbeing": "/uploads/icons/health.svg",
    "Services": "/uploads/icons/services.svg",
    "Activities": "/uploads/icons/activities.svg",
    "Food & Beverage": "/uploads/icons/food&beverage.svg",
    "Online Offers": "/uploads/icons/online.svg",
    "Travel": "/uploads/icons/travel.svg",
    "Retail": "/uploads/icons/retail.svg",
};

const referenceTiles = [
    { title: "Travel", icon: TravelIcon },
    { title: "Health & Wellbeing", icon: HealthIcon },
    { title: "Retail", icon: RetailIcon },
    { title: "Services", icon: ServicesIcon },
    { title: "Activities", icon: ActivitiesIcon },
    { title: "Food & Beverage", icon: FoodAndBeverageIcon },
    { title: "Online Offers", icon: OnlineIcon },
];

const HomepageLayout = () => {
    const dispatch = useDispatch();
    const [tiles, setTiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tileToDelete, setTileToDelete] = useState(null);
    const [allCategories, setAllCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const fetchHomeFilters = async () => {
        setLoading(true);
        try {
            const res = await dispatch(getHomeFilters()).unwrap();

            if (res && res.success && Array.isArray(res.data)) {
                const validTitles = referenceTiles.map(r => r.title.toLowerCase());

                const filteredAndMapped = res.data
                    .filter(item => {
                        const name = (item?.label || item?.title || "").toLowerCase();
                        // Allow plural or singular variations
                        return validTitles.some(t =>
                            t === name ||
                            t === name + "s" ||
                            name === t + "s" ||
                            (t === "health & wellbeing" && (name === "health and wellbeing" || name === "health & wellbeing")) ||
                            (t === "food & beverage" && (name === "food and beverage" || name === "food & beverage"))
                        );
                    })
                    .map((item, idx) => {
                        const itemTitle = item?.label || item?.title || "";
                        const normalizedTitle = itemTitle.toLowerCase();

                        const ref = referenceTiles.find(r => {
                            const rt = r.title.toLowerCase();
                            return rt === normalizedTitle ||
                                rt === normalizedTitle + "s" ||
                                normalizedTitle === rt + "s" ||
                                (rt === "health & wellbeing" && normalizedTitle === "health and wellbeing") ||
                                (rt === "food & beverage" && normalizedTitle === "food and beverage");
                        });

                        const displayTitle = ref ? ref.title : itemTitle;

                        return {
                            ...item,
                            id: item._id,
                            title: displayTitle,
                            icon: iconMap[displayTitle] || iconMap[Object.keys(iconMap).find(k => k.toLowerCase() === displayTitle.toLowerCase())] || TravelIcon,
                            filters: item.filters || []
                        };
                    });
                setTiles(filteredAndMapped);
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            toast.error("Failed to load homepage layout.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await dispatch(getAllCategories()).unwrap();
            if (res?.success && Array.isArray(res?.data)) {
                setAllCategories(res.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchHomeFilters();
    }, [dispatch]);

    const onDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newTiles = [...tiles];
        const draggedItem = newTiles[draggedItemIndex];
        newTiles.splice(draggedItemIndex, 1);
        newTiles.splice(index, 0, draggedItem);

        setDraggedItemIndex(index);
        setTiles(newTiles);
        setHasChanges(true);
    };

    const onDragEnd = () => {
        setDraggedItemIndex(null);
    };

    const showDeleteConfirm = (tile) => {
        setTileToDelete(tile);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!tileToDelete) return;
        try {
            const res = await dispatch(deleteHomeFilter(tileToDelete.id)).unwrap();
            toast.success("Tile removed successfully");
            setHasChanges(true);
            fetchHomeFilters();
        } catch (error) {
            toast.error("An error occurred while removing the tile");
        } finally {
            setIsDeleteModalOpen(false);
            setTileToDelete(null);
        }
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        try {
            const payload = {
                filters: tiles.map((tile, index) => ({
                    id: tile.id,
                    order: index + 1
                }))
            };
            const res = await dispatch(rearrangeHomeFilters(payload)).unwrap();
            if (res?.success) {
                toast.success("Homepage layout saved successfully!");
                setHasChanges(false);
                fetchHomeFilters();
            }
        } catch (error) {
            toast.error("An error occurred while saving changes");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddTile = async () => {
        if (!selectedCategoryId) {
            toast.error("Please select a category");
            return;
        }
        setIsAdding(true);
        try {
            const category = allCategories.find(c => c._id === selectedCategoryId);
            const rawTitle = category?.title || "";

            // Standardize label to match our reference titles if possible
            const categoryTitle = Object.keys(staticIconPathMap).find(
                k => normalizeTitle(k) === normalizeTitle(rawTitle)
            ) || rawTitle;

            // Use the fixed static path for the icon (environment-independent)
            const imageUrl = staticIconPathMap[categoryTitle] ||
                staticIconPathMap[Object.keys(staticIconPathMap).find(k => k.toLowerCase() === categoryTitle.toLowerCase())] ||
                "/uploads/icons/travel.svg";

            const payload = {
                label: categoryTitle,
                image: imageUrl,
                order: 1, // Set to 1 so it goes to the top
                filterCriteria: {}
            };

            const res = await dispatch(addHomeFilter(payload)).unwrap();
            if (res?.success) {
                toast.success("Tile added successfully!");
                setIsAddModalOpen(false);
                setSelectedCategoryId(null);
                setHasChanges(true); // Enable save button for the new tile
                fetchHomeFilters();
            } else {
                toast.error(res?.message || "Failed to add tile");
            }
        } catch (error) {
            console.error("Add tile error:", error);
            toast.error(error?.message || "Failed to add tile");
        } finally {
            setIsAdding(false);
        }
    };

    const blueFilter = "invert(53%) sepia(95%) saturate(2070%) hue-rotate(167deg) brightness(95%) contrast(102%)";

    return (
        <div className="w-full h-full bg-white rounded-[10px] shadow-[0px_0px_50px_rgba(0,0,0,0.08)] border border-[#EAEAEA] flex flex-col items-center pt-2 pb-6 px-1 sm:px-2 lg:px-4 overflow-auto scrollbar-hide">
            <div className="w-full max-w-[1850px] relative px-4">
                <div className="absolute top-2 right-4 z-10">
                    <button
                        onClick={handleSaveChanges}
                        disabled={isSaving || loading || !hasChanges}
                        className={`border border-[#00ADEF] text-[#00ADEF] bg-transparent rounded-[8px] font-[600] h-[40px] px-6 text-[14px] font-poppins transition-all hover:bg-[#00ADEF] hover:text-white shadow-sm whitespace-nowrap ${(isSaving || loading || !hasChanges) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                </div>

                <div className="mb-4 mt-12 px-2">
                    <h2 className="text-[22px] font-bold text-[#303030] mb-0 font-poppins">Active Tiles</h2>
                    <p className="text-[14px] text-[#8C8C8C] font-medium font-poppins">Configure app homepage tiles</p>
                </div>

                <div className="bg-white rounded-[14px] shadow-[0px_10px_40px_rgba(0,0,0,0.03)] border border-[#EAEAEA] p-6 md:p-10 w-full mb-10">
                    <div className="flex justify-end mb-8">
                        <button
                            onClick={() => { setIsAddModalOpen(true); fetchCategories(); }}
                            className="flex items-center gap-2 border border-[#E0E0E0] text-[#00ADEF] bg-white rounded-[8px] font-[600] h-[38px] px-5 text-[14px] font-poppins hover:bg-[#00ADEF] hover:text-white hover:border-[#00ADEF] transition-all whitespace-nowrap"
                        >
                            <PlusOutlined style={{ fontSize: "14px" }} />
                            <span>New Tile</span>
                        </button>
                    </div>

                    <div className="space-y-3">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24 space-y-4">
                                <div className="w-10 h-10 border-4 border-[#00ADEF] border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-[#8C8C8C] font-poppins font-medium">Loading layout items...</p>
                            </div>
                        ) : tiles.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-[#8C8C8C] font-poppins">
                                <p className="text-[16px] font-medium mb-2">No active tiles found.</p>
                                <p className="text-[13px]">Add a new tile to get started.</p>
                            </div>
                        ) : (
                            tiles.map((tile, index) => (
                                <div
                                    key={tile.id || index}
                                    draggable
                                    onDragStart={(e) => onDragStart(e, index)}
                                    onDragOver={(e) => onDragOver(e, index)}
                                    onDragEnd={onDragEnd}
                                    className={`flex items-center p-4 border rounded-[16px] bg-white transition-all duration-300 group ${draggedItemIndex === index
                                        ? "opacity-50 border-[#00ADEF] bg-[#F8FDFF]"
                                        : "border-[#D9D9D9] hover:border-[#00ADEF] hover:shadow-[0_4px_15px_rgba(0,0,0,0.04)]"
                                        }`}
                                >
                                    <div className="cursor-grab active:cursor-grabbing text-[#CED4DA] mr-5 flex items-center justify-center p-1.5 rounded-lg hover:bg-[#F8F9FA] transition-colors">
                                        <HolderOutlined style={{ fontSize: "18px" }} />
                                    </div>

                                    <div className="w-12 h-12 rounded-[14px] bg-[#F0F9FF] flex items-center justify-center mr-5 shadow-sm flex-shrink-0">
                                        <img
                                            src={tile.icon}
                                            alt={tile.title}
                                            className="w-6 h-6 object-contain"
                                            style={{ filter: blueFilter }}
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                                        <h3 className={`font-semibold text-[#434343] font-poppins truncate ${Array.isArray(tile.filters) && tile.filters.length > 0 ? "text-[16px] mb-1.5" : "text-[18px] mb-0"}`}>
                                            {tile.title === "Health and wellbeing" ? "Health & Wellbeing" : tile.title === "Food and Beverage" ? "Food & Beverage" : tile.title}
                                        </h3>
                                        {Array.isArray(tile.filters) && tile.filters.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-[10px] text-[#8C8C8C] font-bold mr-1 uppercase tracking-wider font-poppins whitespace-nowrap">Active Filters:</span>
                                                {tile.filters.map((filter, fIndex) => (
                                                    <span
                                                        key={fIndex}
                                                        className="px-3 py-0.5 rounded-full text-[11px] font-bold font-poppins transition-all duration-200"
                                                        style={{
                                                            backgroundColor: filter.color,
                                                            color: filter.textColor,
                                                        }}
                                                    >
                                                        {filter.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ml-5 flex-shrink-0">
                                        <DeleteIcon
                                            className="w-5 h-5 cursor-pointer hover:scale-110 transition-transform"
                                            onClick={() => showDeleteConfirm(tile)}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* <div className="border-2 border-dashed border-[#EAEAEA] rounded-[24px] p-10 flex flex-col items-center justify-center text-center mt-6 bg-white transition-colors duration-300 w-full">
                        <div className="w-16 h-16 bg-[#F8F9FA] rounded-full flex items-center justify-center mb-6">
                            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 2L6 8L16 14L26 8L16 2Z" stroke="#CED4DA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M6 14L16 20L26 14" stroke="#CED4DA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M6 20L16 26L26 20" stroke="#CED4DA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <p className="text-[#8C8C8C] max-w-sm text-[13px] font-medium leading-relaxed font-poppins">
                            Need more categories? Add a new tile or drag to reorder existing ones.
                        </p>
                    </div> */}
                </div>
            </div>

            <Modal
                title={null} // We will use a custom header for better control
                open={isAddModalOpen}
                onCancel={() => { setIsAddModalOpen(false); setSelectedCategoryId(null); }}
                footer={null} // We will use a custom footer
                closeIcon={null} // Hide default close icon to avoid that square box
                centered
                styles={{
                    mask: { backdropFilter: 'blur(2px)' },
                    content: { borderRadius: '16px', padding: 0, overflow: 'visible' }
                }}
                width={560}
            >
                {/* Custom Close Button */}
                <button
                    onClick={() => { setIsAddModalOpen(false); setSelectedCategoryId(null); }}
                    className="absolute -top-5 -right-5 w-[42px] h-[42px] bg-[#00ADEF] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-300 z-[2001] border-none outline-none cursor-pointer"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="p-8">
                    <h2 className="text-[22px] font-bold text-[#303030] font-poppins mb-2">Add New Tile</h2>
                    <p className="text-[14px] text-[#8C8C8C] font-poppins mb-8">
                        Select a category to add as a tile.
                    </p>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-[15px] font-bold text-[#303030] font-poppins mb-3">Select Category</label>
                            <Select
                                className="w-full h-[52px] font-poppins custom-select-v2"
                                placeholder="Choose a category"
                                onChange={(value) => setSelectedCategoryId(value)}
                                value={selectedCategoryId}
                                showSearch={false}
                                suffixIcon={
                                    <div className="dropdown-arrow-wrapper">
                                        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L7 7L13 1" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                }
                            >
                                {allCategories
                                    .filter(cat => !isCategoryActive(cat.title, tiles))
                                    .map(cat => (
                                        <Select.Option key={cat._id} value={cat._id}>{cat.title}</Select.Option>
                                    ))
                                }
                            </Select>
                        </div>

                        {selectedCategoryId && (() => {
                            const cat = allCategories.find(c => c._id === selectedCategoryId);
                            const previewIcon = cat ? (iconMap[cat.title] || iconMap[Object.keys(iconMap).find(k => k.toLowerCase() === cat?.title?.toLowerCase())] || TravelIcon) : null;
                            return previewIcon ? (
                                <div className="flex items-center gap-4 p-5 bg-[#F0F9FF] rounded-[14px] border border-[#D0EFFF]">
                                    <img src={previewIcon} alt={cat?.title} className="w-11 h-11 object-contain" style={{ filter: "invert(53%) sepia(95%) saturate(2070%) hue-rotate(167deg) brightness(95%) contrast(102%)" }} />
                                    <div>
                                        <p className="text-[15px] font-semibold text-[#434343] font-poppins mb-0">{cat?.title}</p>
                                    </div>
                                </div>
                            ) : null;
                        })()}

                        {/* Custom Footer Buttons */}
                        <div className="flex items-center justify-end gap-3 pt-6">
                            <button
                                onClick={() => { setIsAddModalOpen(false); setSelectedCategoryId(null); }}
                                className="h-[54px] px-10 rounded-[12px] text-[17px] font-semibold font-poppins transition-all duration-300 border-[1.5px] border-[#F0F0F0] bg-white text-[#8C8C8C] hover:bg-[#FF4F4F] hover:text-white hover:border-[#FF4F4F] flex items-center justify-center cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTile}
                                disabled={isAdding}
                                className="h-[54px] px-10 rounded-[12px] text-[17px] font-semibold font-poppins transition-all duration-300 bg-[#00ADEF] text-white border-none hover:bg-[#0099D4] flex items-center justify-center cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isAdding ? <Spin size="small" className="mr-2" /> : "Add Tile"}
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .custom-select-v2 .ant-select-selector {
                        border: 1.5px solid #E0E0E0 !important;
                        height: 52px !important;
                        display: flex !important;
                        align-items: center !important;
                        border-radius: 12px !important;
                        padding: 0 16px !important;
                        transition: all 0.3s ease !important;
                        box-shadow: none !important;
                    }
                    .custom-select-v2:hover .ant-select-selector {
                        border-color: #00ADEF !important;
                    }
                    .custom-select-v2.ant-select-focused .ant-select-selector {
                        border-color: #00ADEF !important;
                    }
                    .custom-select-v2 .ant-select-selection-placeholder {
                        line-height: 50px !important;
                        color: #BFBFBF !important;
                        font-weight: 500 !important;
                    }
                    .custom-select-v2 .ant-select-selection-item {
                        line-height: 50px !important;
                        color: #303030 !important;
                        font-weight: 600 !important;
                    }
                    .dropdown-arrow-wrapper {
                        transition: transform 0.3s ease;
                    }
                    .ant-select-open .dropdown-arrow-wrapper {
                        transform: rotate(180deg);
                    }
                `}</style>
            </Modal>
            {/* Delete Confirmation Modal */}
            <Modal
                title={null}
                open={isDeleteModalOpen}
                onCancel={() => { setIsDeleteModalOpen(false); setTileToDelete(null); }}
                footer={null}
                closeIcon={null}
                centered
                styles={{
                    mask: { backdropFilter: 'blur(3px)' },
                    content: { borderRadius: '24px', padding: 0, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }
                }}
                width={450}
            >
                <div className="p-10 text-center flex flex-col items-center">
                    <div className="w-20 h-20 bg-[#FFF1F0] rounded-full flex items-center justify-center mb-6 shadow-sm border-[4px] border-white ring-1 ring-[#FFCCC7]">
                        <DeleteIcon className="w-9 h-9" style={{ color: '#FF4D4F' }} />
                    </div>

                    <h2 className="text-[24px] font-bold text-[#1A1A1A] font-poppins mb-3 tracking-tight">Remove this tile?</h2>
                    <p className="text-[15px] text-[#666666] font-poppins leading-relaxed mb-10 px-4">
                        Are you sure you want to delete <span className="font-bold text-[#1A1A1A]">"{tileToDelete?.title}"</span>? This will remove it from the homepage layout immediately.
                    </p>

                    <div className="flex gap-3 w-full">
                        <button
                            onClick={() => { setIsDeleteModalOpen(false); setTileToDelete(null); }}
                            className="h-[52px] flex-1 rounded-[14px] text-[15px] font-semibold font-poppins transition-all duration-300 bg-[#F5F5F7] text-[#595959] hover:bg-[#EAEAEA] border-none cursor-pointer"
                        >
                            No, Keep it
                        </button>
                        <button
                            onClick={handleConfirmDelete}
                            className="h-[52px] flex-1 rounded-[14px] text-[15px] font-bold font-poppins transition-all duration-300 bg-[#FF4D4F] text-white hover:bg-[#E03E3E] hover:shadow-[0_8px_20px_-5px_rgba(255,77,79,0.35)] border-none cursor-pointer"
                        >
                            Yes, Remove
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default HomepageLayout;
